/**
 * quantity.js
 *
 * @version 1.0.0
 */

/* ----------------------------------------------------------------------- */
/*  DOM elements                                                           */
/* ----------------------------------------------------------------------- */

const SEL_ACTION_BTN = '[data-quantity-action]'; // plus | minus
const SEL_INPUT = '[data-quantity-input]';

/* ----------------------------------------------------------------------- */
/*  Event handlers                                                         */
/* ----------------------------------------------------------------------- */

/**
 * Handle clicks on + / – buttons.
 * @param {MouseEvent} event
 */
function handleClick(event) {
	const btn = event.target.closest(SEL_ACTION_BTN);
	if (!btn) return;

	const input = btn.closest('[data-quantity]')?.querySelector(SEL_INPUT);

	if (!input) return;

	event.preventDefault();

	const step = readNumberAttr(input, 'step', 1);
	const min = readNumberAttr(input, 'min', -Infinity);
	const max = readNumberAttr(input, 'max', Infinity);

	const dir = btn.dataset.quantityAction === 'plus' ? 1 : -1;
	updateValue(input, dir * step, { min, max, step });
}

/**
 * Sanitise value when the input loses focus or receives direct edits.
 * @param {Event} event
 */
function handleBlurOrChange(event) {
	const input = event.target.closest(SEL_INPUT);
	if (!input) return;

	const step = readNumberAttr(input, 'step', 1);
	const min = readNumberAttr(input, 'min', -Infinity);
	const max = readNumberAttr(input, 'max', Infinity);

	updateValue(input, 0, { min, max, step }); // “delta 0” = just clamp/snap
}

/* ----------------------------------------------------------------------- */
/*  Core helpers                                                           */
/* ----------------------------------------------------------------------- */

/**
 * Clamp to [min,max] and snap to nearest multiple of step.
 *
 * @param {HTMLInputElement} input
 * @param {number}           delta  – amount to add (can be 0)
 * @param {{min:number,max:number,step:number}} opts
 */
function updateValue(input, delta, { min, max, step }) {
	const raw = parseFloat(input.value) || 0;
	let next = raw + delta;

	next = Math.max(min, Math.min(next, max));

	/* Snap to nearest multiple of `step` relative to `min` */
	next = min + Math.round((next - min) / step) * step;

	if (next !== raw) {
		input.value = String(next);
		input.dispatchEvent(new Event('change', { bubbles: true }));
	}
}

/**
 * Read a numeric attribute, falling back to a default.
 * @param {HTMLElement} el
 * @param {string}      name
 * @param {number}      fallback
 */
const readNumberAttr = (el, name, fallback) =>
	parseFloat(el.getAttribute(name)) || fallback;

/* ----------------------------------------------------------------------- */
/*  Public API                                                             */
/* ----------------------------------------------------------------------- */

/**
 * Attach quantity behaviour to `document`.
 *
 * @returns {() => void} – cleanup
 */
export function initQuantities() {
	document.addEventListener('click', handleClick, false);
	document.addEventListener('blur', handleBlurOrChange, true); // capture
	document.addEventListener('change', handleBlurOrChange, false);

	return () => {
		document.removeEventListener('click', handleClick, false);
		document.removeEventListener('blur', handleBlurOrChange, true);
		document.removeEventListener('change', handleBlurOrChange, false);
	};
}
